import React from 'react';
import { Backdrop, CircularProgress, Typography, Box } from '@mui/material';
import { styled } from '@mui/material/styles';

/**
 * BackdropLoader - A blocking loader component using Material-UI Backdrop
 * This component completely blocks user interaction until loading is complete.
 * 
 * @param {Object} props
 * @param {boolean} props.open - Controls whether the backdrop is visible
 * @param {string} props.message - Optional message to display below the spinner
 * @param {string} props.color - Color of the spinner ('primary', 'secondary', 'inherit', etc.)
 * @param {number} props.size - Size of the spinner in pixels
 */

const StyledBackdrop = styled(Backdrop)(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  backgroundColor: 'rgba(0, 0, 0, 0.7)', // Semi-transparent black overlay
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
}));

const LoaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2),
  // Removed white background and shadow for cleaner look
}));

const BackdropLoader = ({ 
  open = false, 
  message = 'Loading...', 
  color = 'primary', 
  size = 40 
}) => {
  return (
    <StyledBackdrop 
      open={open}
      data-testid="backdrop-loader"
      role="progressbar"
      aria-label={message}
      aria-busy={open}
    >
      <LoaderContainer>
        <CircularProgress 
          sx={{ 
            color: '#FFE600', // Yellow color to match the app theme
            marginBottom: '16px'
          }}
          size={size}
          thickness={4}
        />
        {message && (
          <Typography 
            variant="body1" 
            align="center"
            style={{ 
              color: '#FFE600', // Yellow text to match the spinner
              fontWeight: 600,
              userSelect: 'none',
              textShadow: '1px 1px 2px rgba(0, 0, 0, 0.8)' // Shadow for better readability
            }}
          >
            {message}
          </Typography>
        )}
      </LoaderContainer>
    </StyledBackdrop>
  );
};

export default BackdropLoader;
